import { Container, Graphics, Sprite } from "pixi.js";

import { ImageName } from "../../../resources/resources";
import { DEFAULT_LAYER, LayerName } from "../../config/layers";
import { SpriteDef } from "../EntityDef";
import { WithOwner } from "./WithOwner";

/**
 * An extension of Pixi's Container class that integrates with the game's layer system.
 *
 * ## Layer System Integration
 *
 * GameSprite extends Pixi.js containers with layer awareness, allowing sprites to be:
 * - **Automatically sorted** by rendering layer (background, main, HUD, etc.)
 * - **Batch processed** with other sprites in the same layer
 * - **Transformed together** with layer-specific effects (parallax, filters)
 * - **Efficiently managed** by the renderer's sprite lifecycle
 *
 * ## Available Layers
 *
 * The layer system provides these default layers (defined in src/config/layers.ts):
 * - **"main"**: Default layer for game world objects (entities, effects)
 * - **"hud"**: UI elements that don't move with the camera (health bars, menus)
 * - **"debugHud"**: Debug information displayed on top of everything
 *
 * ## Layer Properties
 *
 * Each layer has distinct behavior:
 * - **Parallax**: Controls how the layer responds to camera movement
 *   - main: [1, 1] = moves normally with camera
 *   - hud: [0, 0] = stays fixed on screen
 * - **Z-Order**: Layers render in the order defined in LAYERS config
 * - **Filters**: Each layer can have independent visual effects
 *
 * ## Entity Ownership
 *
 * GameSprites maintain a reference to their owning entity via the WithOwner interface,
 * enabling:
 * - Automatic cleanup when entities are destroyed
 * - Event handling and collision detection
 * - Debugging and performance profiling
 *
 * @example
 * ```typescript
 * // Create a sprite in the main game layer
 * const sprite = loadGameSprite("player.png", "main");
 *
 * // Create a HUD element that stays on screen
 * const healthBar = loadGameSprite("health.png", "hud");
 *
 * // Create debug graphics
 * const debugShape = createGraphics("debugHud");
 * debugShape.drawRect(0, 0, 100, 50);
 * ```
 */
export interface GameSprite extends Container, WithOwner {
  layerName?: LayerName;
}

export type GameGraphics = GameSprite & Graphics;

/**
 * Creates a GameSprite from an image asset with layer assignment.
 *
 * This is the primary way to create visual sprites in the game. The sprite will be
 * automatically added to the specified layer and managed by the rendering system.
 *
 * @param name - Asset name from the resources manifest (must exist in resources/)
 * @param layerName - Which rendering layer to place the sprite in (default: "main")
 * @param options - Optional configuration for anchor point and size
 * @returns A Pixi Sprite with GameSprite layer integration
 *
 * @example
 * ```typescript
 * // Basic sprite creation
 * const playerSprite = loadGameSprite("hero.png");
 *
 * // Sprite with custom layer and anchor
 * const enemySprite = loadGameSprite("enemy.png", "main", {
 *   anchor: [0.5, 0.5], // Center anchor
 *   size: [64, 64]      // Fixed size in pixels
 * });
 *
 * // HUD element
 * const hudElement = loadGameSprite("ui_panel.png", "hud");
 * ```
 */
export function loadGameSprite(
  name: ImageName,
  layerName?: LayerName,
  options?: { anchor?: [number, number]; size?: [number, number] },
): Sprite & GameSprite {
  const sprite = Sprite.from(name) as Sprite & GameSprite;
  sprite.layerName = layerName;
  if (options?.anchor) {
    sprite.anchor.set(...options.anchor);
  }
  if (options?.size) {
    sprite.setSize(...options.size);
  }
  return sprite;
}

/**
 * Creates a GameSprite from a sprite definition object.
 *
 * Used internally by the entity system to create sprites from EntityDef configurations.
 * This allows entities to define their visual appearance declaratively.
 *
 * @param spriteDef - Sprite definition containing image, layer, and display properties
 * @returns A GameSprite configured according to the definition
 *
 * @example
 * ```typescript
 * const spriteDef: SpriteDef = {
 *   image: "bullet.png",
 *   layer: "main",
 *   anchor: [0.5, 0.5],
 *   size: [8, 8]
 * };
 * const sprite = spriteFromDef(spriteDef);
 * ```
 */
export function spriteFromDef(spriteDef: SpriteDef): GameSprite {
  return loadGameSprite(spriteDef.image, spriteDef.layer, {
    anchor: spriteDef.anchor,
    size: spriteDef.size,
  });
}

/**
 * Creates a GameSprite Graphics object for programmatic drawing.
 *
 * Use this when you need to draw shapes, lines, or other graphics procedurally
 * rather than loading from image assets. Commonly used for:
 * - Simple geometric shapes (circles, rectangles, lines)
 * - Debug visualization (collision bounds, paths, vectors)
 * - Dynamic UI elements (progress bars, custom buttons)
 * - Particle effects and trails
 *
 * @param layerName - Which rendering layer to place the graphics in
 * @returns A Pixi Graphics object with GameSprite layer integration
 *
 * @example
 * ```typescript
 * // Create a simple colored circle
 * const circle = createGraphics("main");
 * circle.fill(0x00ff00);
 * circle.drawCircle(0, 0, 10);
 *
 * // Debug collision bounds
 * const debugBounds = createGraphics("debugHud");
 * debugBounds.stroke({ color: 0xff0000, width: 2 });
 * debugBounds.drawRect(-25, -25, 50, 50);
 *
 * // HUD progress bar
 * const healthBar = createGraphics("hud");
 * healthBar.fill(0x00ff00);
 * healthBar.drawRect(10, 10, 200 * healthPercent, 20);
 * ```
 */
export function createGraphics(
  layerName: LayerName = DEFAULT_LAYER,
): GameSprite & Graphics {
  const graphics = new Graphics() as GameSprite & Graphics;
  graphics.layerName = layerName;
  return graphics;
}
